import { useEffect, useState } from "react";

import { api } from "../api";
import LessonProgressRing from "../components/LessonProgressRing";
import type { Lesson } from "../types";
import { useMainButton } from "../tg";

type Props = {
  // Кеш из App: если уже есть — рендерим мгновенно, без "Загрузка…".
  // Свежий запрос всё равно полетит в useEffect и обновит и локальный state, и App-кеш.
  initialLessons?: Lesson[];
  // Имя раздела из БД (админка может его поменять). Если ещё не подгрузили
  // sections — рендерим fallback, потом перерисуется тихо.
  sectionTitle: string | undefined;
  onLessonsLoaded: (lessons: Lesson[]) => void;
  onOpenLesson: (lesson: Lesson) => void;
  onEditSelection: () => void;
  onNoSelection: () => void;
};

export default function MyParasites({
  initialLessons,
  sectionTitle,
  onLessonsLoaded,
  onOpenLesson,
  onEditSelection,
  onNoSelection,
}: Props) {
  const [lessons, setLessons] = useState<Lesson[] | null>(initialLessons ?? null);
  const [error, setError] = useState<string | null>(null);

  useMainButton(null);

  // Тихий revalidate. App-кеш обновляем всегда, даже если экран успели размонтировать —
  // иначе кеш в App не успеет заполниться при быстром «вошёл-вышел-вошёл».
  useEffect(() => {
    let cancelled = false;
    api<Lesson[]>("/sections/parasites/lessons")
      .then((data) => {
        onLessonsLoaded(data);
        if (cancelled) return;
        setLessons(data);
        if (data.filter((l) => l.chosen).length === 0) {
          onNoSelection();
        }
      })
      .catch((e: Error) => {
        if (!cancelled && !lessons) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // Запрос делаем один раз при маунте — кеш в App переживает unmount при свайпе.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chosen = (lessons ?? []).filter((l) => l.chosen);

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <div className="header-row">
          <div className="header-titles">
            <h1>{sectionTitle ?? "Мои паразиты"}</h1>
            <p className="subtitle">Слова, с которыми работаем</p>
          </div>
          <button
            type="button"
            className="header-action-btn"
            onClick={onEditSelection}
            aria-label="Изменить выбор"
          >
            <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">
              <path
                d="M7 2v10M2 7h10"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {error && <div className="error-card">{error}</div>}

      {!lessons && !error && <div className="card">Загрузка…</div>}

      {lessons && chosen.length === 0 && (
        <div className="card">
          <p>Список пуст.</p>
          <button type="button" className="btn-secondary" onClick={onEditSelection}>
            Выбрать паразитов
          </button>
        </div>
      )}

      {lessons && chosen.length > 0 && (
        <div className="lesson-cards">
          {chosen.map((lesson) => {
            const total = lesson.blocks_total;
            const done = lesson.blocks_completed;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            const isDone = total > 0 && done >= total;
            const isEmpty = done === 0;
            // Для завершённого урока CTA не показываем — статус «пройдено»
            // уже передаёт зелёное кольцо с галочкой справа.
            const cta = isDone ? null : done > 0 ? "Продолжить" : "Начать";
            return (
              <button
                key={lesson.id}
                type="button"
                className={`lesson-card lesson-card-with-ring ${isDone ? "lesson-card-done" : ""}`}
                onClick={() => onOpenLesson(lesson)}
              >
                <div className="lesson-card-main">
                  <span className="lesson-card-title">{lesson.title}</span>
                  <div className="lesson-card-meta">
                    {done}/{total} блоков
                    {cta && <> · {cta}</>}
                  </div>
                </div>
                <LessonProgressRing
                  total={total}
                  done={done}
                  pct={pct}
                  isDone={isDone}
                  isEmpty={isEmpty}
                />
              </button>
            );
          })}
        </div>
      )}

    </div>
  );
}
